import { SunIcon, MoonIcon, MonitorIcon } from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

type ThemeChoice = 'light' | 'dark' | 'system';

/**
 * Theme cycle button. Clicking advances light → dark → system → light.
 *
 * Hydration safety:
 * - next-themes resolves the theme client-side AFTER first render to avoid SSR mismatches.
 * - We render a static placeholder until `mounted` is true to keep the initial markup stable.
 *   This is the canonical next-themes pattern; not following it produces a console warning in dev.
 */
export function ThemeToggle(): JSX.Element {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const current: ThemeChoice = mounted
    ? ((theme as ThemeChoice | undefined) ?? 'system')
    : 'system';
  const next: ThemeChoice = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
  const Icon = current === 'light' ? SunIcon : current === 'dark' ? MoonIcon : MonitorIcon;
  const label = current === 'light' ? 'Light' : current === 'dark' ? 'Dark' : 'System';

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        setTheme(next);
      }}
      aria-label={`Theme: ${label}. Click to switch to ${next}.`}
      title={`Theme: ${label}`}
      data-testid="theme-toggle"
    >
      <Icon size={18} weight="duotone" />
    </Button>
  );
}
